import { z } from "zod";

/**
 * Field definitions for a career level (WI-102). Deliberately **default-free**:
 * `.partial()` in zod v4 still fires `.default()`s, so an update schema derived
 * from a base carrying defaults would silently re-inject values the caller
 * never sent. Any default belongs at the DB layer, not here.
 */
const roleLevelFields = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  definition: z.string().trim().max(2000).nullable(),
});

/** Create payload — name required, definition optional-but-explicit. */
export const createRoleLevelSchema = roleLevelFields;
export type CreateRoleLevelInput = z.infer<typeof createRoleLevelSchema>;

/** Edit payload — only the fields present are written. */
export const updateRoleLevelSchema = roleLevelFields.partial();
export type UpdateRoleLevelInput = z.infer<typeof updateRoleLevelSchema>;

/**
 * Reorder payload: the complete list of level ids in their new ladder order.
 * Sent whole rather than as a single moved id so the resulting `sortOrder`
 * values are unambiguous and one request can't leave the ladder half-renumbered.
 */
export const reorderRoleLevelsSchema = z.object({
  ids: z.array(z.string().min(1)).min(1, "At least one level is required"),
});
export type ReorderRoleLevelsInput = z.infer<typeof reorderRoleLevelsSchema>;

/** Retire / restore a level. Retirement is soft — levels are never deleted. */
export const setRoleLevelRetiredSchema = z.object({
  retired: z.boolean(),
});

/**
 * Which levels a role opts into (WI-104) — the complete set, replacing whatever
 * the role had. An empty array is valid and means the role uses no levels.
 *
 * Deliberately an unordered set with no contiguity rule: Data Analyst uses
 * Associate + Senior with Staff skipped, so validating a range here would be
 * wrong.
 */
export const setRoleLevelsSchema = z.object({
  roleLevelIds: z.array(z.string().min(1)),
});
export type SetRoleLevelsInput = z.infer<typeof setRoleLevelsSchema>;
